import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Sparkles } from "lucide-react";
import { fetchWhatsAppStats, trackWhatsAppClick } from "../lib/api";

const WHATSAPP_NUMBER = "33624113623";

const buildMessage = (termName) =>
  `Bonjour Beckyliss, je découvre votre glossaire et je souhaite un conseil personnalisé sur le produit ${termName}. Pouvez-vous m'aider à composer ma routine ?`;

export const RoutineBuilder = ({ termName, products = [] }) => {
  const [counter, setCounter] = useState(null);

  useEffect(() => {
    fetchWhatsAppStats().then(setCounter).catch(() => setCounter(null));
  }, []);

  if (products.length === 0) return null;
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildMessage(termName))}`;

  const handleClick = () => {
    trackWhatsAppClick().then(setCounter).catch(() => {});
  };

  return (
    <section
      className="border-t border-bl-border bg-gradient-to-b from-bl-bg to-bl-rose/30 py-20 md:py-24"
      data-testid="routine-builder"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <span className="overline flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.5} /> Sur-mesure Beckyliss
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-3 leading-tight text-balance">
              Composer ma routine
            </h2>
            <p className="mt-4 text-base text-bl-textSoft leading-relaxed max-w-xl">
              Pour aller plus loin que cette définition, voici quelques compagnons
              de routine signés Beckyliss. Une question ? Notre équipe vous conseille
              en privé sur WhatsApp.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col md:items-end gap-3">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="inline-flex items-center gap-3 bg-bl-turquoise text-white px-7 py-3.5 text-sm tracking-wide hover:bg-bl-text transition-colors"
              data-testid="whatsapp-cta"
            >
              <MessageCircle size={18} strokeWidth={1.5} />
              Demander un conseil sur WhatsApp
            </a>
            {counter && (
              <p
                className="text-xs tracking-wide text-bl-textSoft md:text-right"
                data-testid="whatsapp-counter"
              >
                <span className="font-serif italic text-bl-dore text-base">
                  +{counter.displayed}
                </span>{" "}
                conseils délivrés ce mois-ci
              </p>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {products.map((p, i) => (
            <Link
              key={p.slug}
              to={`/produit/${p.slug}`}
              className="group bg-bl-surface border border-bl-border overflow-hidden flex flex-col"
              data-testid={`routine-product-${p.slug}`}
            >
              <div className="aspect-square overflow-hidden bg-bl-soft">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover product-image-zoom"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-bl-textSoft text-sm">
                    Beckyliss
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <span className="overline flex items-center gap-2">
                    Étape {i + 1}
                    <span className="h-px w-6 bg-bl-dore" />
                  </span>
                  <h3 className="font-serif text-xl text-bl-text mt-3 leading-snug min-h-[3rem]">
                    {p.name}
                  </h3>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-bl-text font-medium">
                    {p.price ? `${p.price.toFixed(2).replace('.', ',')} €` : '—'}
                  </span>
                  <span className="text-xs tracking-wide text-bl-dore underline-grow">
                    Découvrir
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-12 text-xs text-bl-textSoft text-center max-w-xl mx-auto leading-relaxed">
          Chaque routine se compose dans le temps. Nos conseillères beauté Beckyliss
          répondent en moyenne en moins de 2 heures, du lundi au samedi.
        </p>
      </div>
    </section>
  );
};
